import React, { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fmtKickoff } from '../lib/datetime'
import { useAuth } from '../contexts/AuthContext'
import UnlockModal from '../components/UnlockModal'
import { hasPaidAnalyticsAccess } from '../lib/lockedPickLink'
import { Clock, Lock, Sparkles, Crown, ChevronRight, Zap, Bot, TrendingUp, BarChart3 } from 'lucide-react'

async function fetchJson(url) {
  const r = await fetch(url, { credentials: 'include', headers: { Accept: 'application/json' } })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}
const fmtZAR = n => { const abs = Math.abs(n).toFixed(2); const [int, dec] = abs.split('.'); return `R ${int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}${dec === '00' ? '' : `.${dec}`}` }
const pad2 = n => String(Math.max(0, n)).padStart(2, '0')
const hasValidDate = value => value != null && value !== '' && Number.isFinite(new Date(value).getTime())
function useCountdown(isoDate) {
  const [t, setT] = useState({ h: 0, m: 0, s: 0 })
  useEffect(() => {
    if (!hasValidDate(isoDate)) {
      setT({ h: 0, m: 0, s: 0 })
      return
    }
    const kickoffTime = new Date(isoDate).getTime()
    const tick = () => {
      const d = Math.max(0, kickoffTime - Date.now())
      setT({ h: Math.floor(d / 3600000), m: Math.floor(d % 3600000 / 60000), s: Math.floor(d % 60000 / 1000) })
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [isoDate])
  return t
}
function Countdown({ isoDate }) {
  const { h, m, s } = useCountdown(isoDate)
  return (
    <div className="sb-countdown">
      {[[h, 'HRS'], [m, 'MINS'], [s, 'SECS']].map(([value, label]) => (
        <span key={label}>
          <strong>{pad2(value)}</strong>
          <small>{label}</small>
        </span>
      ))}
    </div>
  )
}
function CircularMeter({ pct = 0, size = 86 }) {
  const sw = 7, r = (size - sw) / 2, circ = 2 * Math.PI * r, offset = circ - Math.min(100, Math.max(0, pct)) / 100 * circ
  return <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}><circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#2c3555" strokeWidth={sw}/><circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#3d7fff" strokeWidth={sw} strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" transform={`rotate(-90 ${size / 2} ${size / 2})`}/><text x="50%" y="57%" textAnchor="middle" fill="#fff" fontSize={size / 4} fontWeight="700" fontFamily="IBM Plex Mono">{pct}%</text></svg>
}
function MiniSparkline({ values, color }) { if (!values || values.length < 2) return <div className="h-8"/>; const min = Math.min(...values), range = Math.max(...values) - min || 1; const pts = values.map((v, i) => `${i / (values.length - 1) * 200},${32 - (v - min) / range * 32}`).join(' '); return <svg viewBox="0 0 200 32" className="w-full h-8"><polyline points={pts} fill="none" stroke={color} strokeWidth="2.5"/></svg> }
function StatusBadge({ status }) { return <span className={`sb-status ${status}`}>{status === 'pending' ? 'In progress' : status === 'won' ? 'Won' : 'Lost'}</span> }
function TeamInitials({ name, size = 8 }) { return <div className="sb-initials" style={{ width: size * 4, height: size * 4 }}>{name?.slice(0, 2).toUpperCase() || '??'}</div> }
function TeamLogo({ logo, name, size = 8 }) { const [err, setErr] = useState(false); return logo && !err ? <img src={logo} alt={name || ''} width={size * 4} height={size * 4} onError={() => setErr(true)} className="sb-logo"/> : <TeamInitials name={name} size={size}/> }

export default function Dashboard() {
  const router = useRouter()
  const { user: authUser } = useAuth()
  const queryClient = useQueryClient()
  const userId = authUser?.user_id
  const isPremium = hasPaidAnalyticsAccess(authUser)
  const [unlockModal, setUnlockModal] = useState({ isOpen: false, matchId: null, coinCost: 1 })
  const { data: userUnlocks, isSuccess: unlocksLoaded, isFetching: unlocksFetching } = useQuery({ queryKey: ['skillcoins', 'unlocks', userId], queryFn: () => fetchJson('/api/skillcoins/unlocks'), enabled: !!userId, staleTime: 0, refetchOnMount: 'always', retry: false })
  const unlockedMatches = unlocksLoaded && !unlocksFetching && userUnlocks?.success === true
    ? [...(userUnlocks.match_ids || []), ...(userUnlocks.named_unlocks || [])] : []
  const handleUnlock = (matchId, cost = 1) => setUnlockModal({ isOpen: true, matchId, coinCost: cost })
  const handleUnlocked = () => { queryClient.invalidateQueries({ queryKey: ['skillcoins', 'unlocks', userId] }); queryClient.invalidateQueries({ queryKey: ['match-of-the-day', userId] }); queryClient.invalidateQueries({ queryKey: ['daily-ai-six', userId] }) }
  const { data: user } = useQuery({ queryKey: ['user', 'profile', userId], queryFn: () => fetchJson(`/api/user/profile?user_id=${userId}`), enabled: !!userId, staleTime: 300000 })
  const { data: highlightsData, isLoading: highlightsLoading } = useQuery({ queryKey: ['highlights', 'random'], queryFn: () => fetchJson('/api/highlights/random?count=8'), staleTime: 120000 })
  const { data: userPredictions = [], isLoading: predsLoading } = useQuery({ queryKey: ['predictions', userId], queryFn: () => fetchJson(`/api/predictions?user_id=${userId}&limit=100`), enabled: !!userId, staleTime: 60000 })
  const { data: motd, isLoading: motdLoading } = useQuery({ queryKey: ['match-of-the-day', userId], queryFn: () => fetchJson('/api/match-of-the-day'), enabled: !!userId, staleTime: 300000, refetchOnMount: 'always' })
  const { data: aiSix, isLoading: aiSixLoading } = useQuery({ queryKey: ['daily-ai-six', userId], queryFn: () => fetchJson('/api/daily-ai-six'), enabled: !!userId, staleTime: 300000, refetchOnMount: 'always' })
  const { data: botStats, isLoading: botStatsLoading } = useQuery({ queryKey: ['bot', 'stats'], queryFn: () => fetchJson('/api/bot/stats'), staleTime: 300000 })
  const { data: botBets = [], isLoading: botBetsLoading } = useQuery({ queryKey: ['bot', 'recent-bets'], queryFn: () => fetchJson('/api/bot/recent-bets'), staleTime: 120000, select: d => d.bets || [] })
  const upcomingFixtures = useMemo(() => { const base = highlightsData?.matches || [], ids = new Set(base.map(f => String(f.id))); const extra = botBets.filter(b => b.status === 'pending' && b.kickoff && new Date(b.kickoff) > new Date() && (!b.match_id || !ids.has(String(b.match_id)))).map((b, i) => { const [homeName, awayName] = (b.match || '').split(' vs '); const id = b.match_id ? String(b.match_id) : `bot-${b.id || i}`; ids.add(id); return { id, fixtureId: b.match_id || null, leagueId: b.league_id || null, date: b.kickoff, league: { id: b.league_id || null, name: b.league || 'Football' }, teams: { home: { name: homeName || 'Home', logo: b.home_logo }, away: { name: awayName || 'Away', logo: b.away_logo } } } }); return [...base, ...extra].slice(0, 6) }, [highlightsData, botBets])
  const { metrics, userSparkline } = useMemo(() => { const recent = userPredictions.filter(p => new Date(p.created_at).getTime() >= Date.now() - 30 * 86400000), settled = recent.filter(p => p.status === 'won' || p.status === 'lost'), won = settled.filter(p => p.status === 'won'), stake = settled.reduce((s, p) => s + (p.stake_zar || 0), 0) / 100, ret = won.reduce((s, p) => s + (p.payout_zar || 0), 0) / 100; let cum = 0; const points = settled.slice().sort((a, b) => new Date(a.created_at) - new Date(b.created_at)).map(p => { cum += ((p.status === 'won' ? p.payout_zar || 0 : 0) - (p.stake_zar || 0)) / 100; return cum }); return { metrics: { pnl30d: ret - stake, winRate30d: settled.length ? Math.round(won.length / settled.length * 100) : 0, openSims: recent.filter(p => p.status === 'pending').length }, userSparkline: points.length > 1 ? points : [0, 0] } }, [userPredictions])
  const botSparkline = useMemo(() => { let c = 0; const p = botBets.filter(b => b.status === 'won' || b.status === 'lost').sort((a, b) => new Date(a.placed_at) - new Date(b.placed_at)).slice(-20).map(b => { c += b.status === 'won' ? b.payout_zar - b.stake_zar : -b.stake_zar; return c }); return p.length > 1 ? p : [0, 0] }, [botBets])
  const botWinRate = Math.round(botStats?.stats?.hit_rate || 0), botPnl = botStats?.stats?.profit_zar || 0
  const lastResult = useMemo(() => userPredictions.filter(p => p.status === 'won' || p.status === 'lost').sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0], [userPredictions])
  const botPending = useMemo(() => botBets.filter(b => b.status === 'pending').slice(0, 4), [botBets])
  const botResults = useMemo(() => botBets.filter(b => b.status === 'won' || b.status === 'lost').sort((a, b) => new Date(b.settled_at || b.placed_at) - new Date(a.settled_at || a.placed_at)).slice(0, 5), [botBets])
  const aiMatches = aiSix?.matches || [], unlockLevel = aiSix?.unlockLevel ?? (aiSix?.isLocked === false ? 6 : 0)
  const featMatchUnlocked = isPremium || unlockedMatches.includes('daily-ai-six-1'), featMatchesUnlocked = isPremium || unlockedMatches.includes('daily-ai-six-3'), aiSixUnlocked = isPremium || unlockLevel >= 6 || unlockedMatches.includes('daily-ai-six'), motdUnlocked = !motd?.isLocked || unlockedMatches.includes(motd?.match?.fixture_id)
  const analyticsHref = match => `/analytics?match=${match.fixture_id}${match.league_id ? `&league=${match.league_id}` : ''}`
  const rawMotdConfidence = motd?.match?.confidence
  const motdConfidence = rawMotdConfidence == null || rawMotdConfidence === '' ? null : Number(rawMotdConfidence)
  const hasMotdConfidence = motdUnlocked && Number.isFinite(motdConfidence)
  const comparisonLoading = predsLoading || botStatsLoading || botBetsLoading
  const fixturesLoading = highlightsLoading || botBetsLoading

  return (
    <main className="sb-page">
      <div className="sb-disclaimer">
        <b>Simulations &amp; analytics only.</b> Virtual currency — no cash wagering or payouts.
      </div>

      <div className="sb-wrap">
        <section className="sb-card sb-compare">
          <div className="sb-head">
            <div>
              <h2>You vs SkillBot</h2>
              <p>See how your predictions stack up against the model&apos;s, on identical fixtures.</p>
            </div>
            <Link href="/sports-hub" className="sb-link">
              <Bot size={14} /> SkillBot AI
            </Link>
          </div>

          <div className="sb-vs" data-testid={comparisonLoading ? 'comparison-loading' : 'comparison-resolved'}>
            {comparisonLoading ? (
              <div className="sb-skeleton" />
            ) : (
              <>
            <div className="sb-panel sb-you" data-testid="comparison-you-panel">
              <div className="sb-who">You</div>
                  <div className="sb-stats">
                    <div><small>Win rate</small><strong>{metrics.winRate30d}%</strong></div>
                    <div>
                      <small>P&amp;L (30d)</small>
                      <strong className={metrics.pnl30d >= 0 ? 'positive' : 'negative'}>
                        {metrics.pnl30d >= 0 ? '+' : '-'}{fmtZAR(Math.abs(metrics.pnl30d))}
                      </strong>
                    </div>
                  </div>
                  <MiniSparkline values={userSparkline} color={metrics.pnl30d >= 0 ? '#34d399' : '#f2596b'} />
            </div>

            <div className="sb-vsmark" data-testid="comparison-vs-badge">VS</div>

            <div className="sb-panel sb-bot" data-testid="comparison-bot-panel">
              <div className="sb-who"><Bot size={14} /> SkillBot</div>
                  <div className="sb-stats">
                    <div><small>Win rate</small><strong>{botWinRate}%</strong></div>
                    <div>
                      <small>P&amp;L (30d)</small>
                      <strong className={botPnl >= 0 ? 'positive' : 'negative'}>
                        {botPnl >= 0 ? '+' : '-'}{fmtZAR(Math.abs(botPnl))}
                      </strong>
                    </div>
                  </div>
                  <MiniSparkline values={botSparkline} color={botPnl >= 0 ? '#34d399' : '#f2596b'} />
            </div>
              </>
            )}
          </div>

          <div className="sb-legend">
            <span><i className="dot blue" />Advanced predictions</span>
            <span><i className="dot green" />High-confidence picks</span>
            <span><i className="dot gold" />Track your own accuracy</span>
          </div>
        </section>

        <section className="sb-card sb-motd">
          <div className="sb-head">
            <div>
              <div className="sb-eyebrow gold">Match of the Day</div>
              <p>{motd?.match?.league || 'Featured fixture'}</p>
            </div>
            <span className="sb-vip"><Crown size={13} /> Hero</span>
          </div>

          {motdLoading ? (
            <div className="sb-skeleton" />
          ) : !motd?.hasMatch ? (
            <div className="sb-empty">No Match of the Day right now.</div>
          ) : (
            <div className="sb-motd-body">
              <div className="sb-teams">
                <div className="sb-team">
                  <TeamLogo logo={motd.match.home_logo} name={motd.match.home_team} size={13} />
                  <b>{motd.match.home_team}</b>
                </div>
                <div className="sb-vslabel">
                  vs
                  <small>{fmtKickoff(motd.match.kickoff)}</small>
                </div>
                <div className="sb-team">
                  <TeamLogo logo={motd.match.away_logo} name={motd.match.away_team} size={13} />
                  <b>{motd.match.away_team}</b>
                </div>
              </div>

              <div className="sb-confidence">
                <small>SkillBot confidence</small>
                {hasMotdConfidence ? (
                  <>
                    <CircularMeter pct={motdConfidence} />
                    <span><Zap size={12} /> High confidence pick</span>
                  </>
                ) : (
                  <div className="sb-confidence-locked">
                    {!motdUnlocked && <Lock size={20} />}
                    <span>{motdUnlocked ? 'Not available' : 'Unlock to view'}</span>
                  </div>
                )}
              </div>

              <div className="sb-motd-actions">
                {hasValidDate(motd.match.kickoff) && (
                  <div className="sb-expire">
                    <Clock size={12} /> Expires in <Countdown isoDate={motd.match.kickoff} />
                  </div>
                )}
                {!motdUnlocked ? (
                  <>
                    <div className="sb-locked"><Lock size={12} /> Prediction locked</div>
                    <button
                      className="sb-btn gold-btn"
                      onClick={() => handleUnlock(motd.match.fixture_id, 1)}
                      data-testid="button-unlock-match-of-day"
                    >
                      Unlock pick · 1 SkillCoin
                    </button>
                  </>
                ) : (
                  <>
                    <div className="sb-pick">
                      <Sparkles size={14} />{motd.match.prediction}
                      {hasMotdConfidence && <small>({motdConfidence}%)</small>}
                    </div>
                    <button
                      className="sb-btn blue-btn"
                      onClick={() => router.push(`/analytics?match=${motd.match.fixture_id}`)}
                      data-testid="button-view-analysis"
                    >
                      View full analysis
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </section>

        <div className="sb-tiers">
          <section className="sb-card sb-tier feature">
            <div className="sb-tier-title">Feature Match</div>
            <p>Unlock 1 high-confidence pick.</p>
            {aiSixLoading ? (
              <div className="sb-skeleton short" />
            ) : aiMatches[0] ? (
              <>
                <div className="sb-mini-teams">
                  <div>
                    <TeamLogo logo={aiMatches[0].home_logo} name={aiMatches[0].home_team} size={10} />
                    <b>{aiMatches[0].home_team}</b>
                  </div>
                  <span>vs</span>
                  <div>
                    <TeamLogo logo={aiMatches[0].away_logo} name={aiMatches[0].away_team} size={10} />
                    <b>{aiMatches[0].away_team}</b>
                  </div>
                </div>
                {featMatchUnlocked ? (
                  <Link href={analyticsHref(aiMatches[0])} className="sb-pick centered sb-pick-link">
                    {aiMatches[0].prediction} · {aiMatches[0].confidence}%
                  </Link>
                ) : (
                  <>
                    {hasValidDate(aiMatches[0].kickoff) && (
                      <div className="sb-tier-countdown">
                        <small>Picks unlock in</small>
                        <Countdown isoDate={aiMatches[0].kickoff} />
                      </div>
                    )}
                    <button
                      className="sb-unlock gold-btn"
                      onClick={() => handleUnlock('daily-ai-six-1', 1)}
                      data-testid="button-unlock-feature-match"
                    >
                      Unlock with 1 SkillCoin
                    </button>
                  </>
                )}
              </>
            ) : (
              <div className="sb-empty">No pick available</div>
            )}
          </section>

          <section className="sb-card sb-tier">
            <div className="sb-tier-title blue">AI Three</div>
            <p>Unlock 3 high-confidence picks.</p>
            {aiSixLoading ? (
              <div className="sb-skeleton short" />
            ) : (
              <>
                {Array.from({ length: 3 }).map((_, i) => {
                  const match = aiMatches[i + 1]
                  if (!match) {
                    return (
                      <div className="sb-pick-row muted" key={i}>
                        <span>{i + 1}</span><span>Pick available soon</span><Lock size={12} />
                      </div>
                    )
                  }
                  const content = (
                    <>
                      <span>{i + 1}</span>
                      <span>{match.home_team} <em>vs</em> {match.away_team}</span>
                      {featMatchesUnlocked ? <b>{match.prediction?.split(' ')[0]}</b> : <Lock size={12} />}
                    </>
                  )
                  return featMatchesUnlocked ? (
                    <Link href={analyticsHref(match)} className="sb-pick-row sb-pick-link" key={i}>
                      {content}
                    </Link>
                  ) : (
                    <div className="sb-pick-row" key={i}>{content}</div>
                  )
                })}
                {!featMatchesUnlocked && aiMatches.length > 1 && (
                  <button
                    className="sb-unlock gold-btn"
                    onClick={() => handleUnlock('daily-ai-six-3', 3)}
                    data-testid="button-unlock-feature-matches"
                  >
                    Unlock all 3 picks · 3 SkillCoins
                  </button>
                )}
              </>
            )}
          </section>

          <section className="sb-card sb-tier ai-six">
            <div className="sb-tier-title gold">AI Six</div>
            <p>Unlock 6 high-confidence picks.</p>
            {aiSixLoading ? (
              <div className="sb-skeleton short" />
            ) : (
              <>
                {Array.from({ length: 6 }).map((_, i) => {
                  const match = aiMatches[i]
                  const content = (
                    <>
                      <span>{i + 1}</span>
                      <span>{match ? <>{match.home_team} <em>vs</em> {match.away_team}</> : 'Pick available soon'}</span>
                      {match && aiSixUnlocked ? <b>{match.prediction?.split(' ')[0]}</b> : <Lock size={12} />}
                    </>
                  )
                  return match && aiSixUnlocked ? (
                    <Link href={analyticsHref(match)} className="sb-pick-row sb-pick-link" key={i}>
                      {content}
                    </Link>
                  ) : (
                    <div className={`sb-pick-row${match ? '' : ' muted'}`} key={i}>{content}</div>
                  )
                })}
                {!aiSixUnlocked && aiMatches.length > 0 && (
                  <button
                    className="sb-unlock gold-btn"
                    onClick={() => handleUnlock('daily-ai-six', 6)}
                    data-testid="button-unlock-all-ai-six"
                  >
                    Unlock AI Six · 6 SkillCoins
                  </button>
                )}
              </>
            )}
          </section>
        </div>

        <section className="sb-card">
          <div className="sb-head">
            <h2>Upcoming Fixtures</h2>
            <button
              className="sb-text-btn"
              onClick={() => router.push('/predictions/make')}
              data-testid="button-see-all-fixtures"
            >
              See all <ChevronRight size={14} />
            </button>
          </div>

          {fixturesLoading ? (
            <div className="sb-skeleton" data-testid="fixtures-loading" />
          ) : !upcomingFixtures.length ? (
            <div className="sb-empty" data-testid="fixtures-empty">No upcoming fixtures found.</div>
          ) : (
            <div className="sb-fixtures" data-testid="fixtures-resolved">
              {upcomingFixtures.map(fixture => (
                <div className="sb-fixture" key={fixture.id}>
                  <div className="sb-fixture-meta">
                    <b>{fixture.league?.name}</b>
                    <span><Clock size={11} /> {fmtKickoff(fixture.date)}</span>
                  </div>
                  <div className="sb-fixture-teams">
                    <span>
                      <TeamLogo logo={fixture.teams?.home?.logo} name={fixture.teams?.home?.name} size={7} />
                      {fixture.teams?.home?.name}
                    </span>
                    <em>vs</em>
                    <span>
                      <TeamLogo logo={fixture.teams?.away?.logo} name={fixture.teams?.away?.name} size={7} />
                      {fixture.teams?.away?.name}
                    </span>
                  </div>
                  <button
                    className="sb-simulate"
                    onClick={() => {
                      const fixtureId = fixture.fixtureId ?? (String(fixture.id).startsWith('bot-') ? null : fixture.id)
                      const leagueId = fixture.league?.id ?? fixture.leagueId
                      router.push(`/predictions/make${fixtureId || leagueId ? `?${fixtureId ? `fixture=${fixtureId}` : ''}${fixtureId && leagueId ? '&' : ''}${leagueId ? `league=${leagueId}` : ''}` : ''}`)
                    }}
                    data-testid={`button-simulate-${fixture.id}`}
                  >
                    Simulate
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="sb-bottom">
          <section className="sb-card">
            <div className="sb-head">
              <h2><Bot size={15} /> SkillBot pending Picks</h2>
              <span className="sb-live">Live</span>
            </div>
            {botBetsLoading ? (
              <div className="sb-skeleton short" data-testid="pending-bets-loading" />
            ) : botPending.length ? (
              botPending.map((bet, i) => (
                <div className="sb-pending" key={bet.id || i}>
                  <span>{bet.match}</span>
                  {isPremium || unlockedMatches.includes('bot-pending') ? (
                    <b>{bet.pick?.toUpperCase()} · {bet.odds}</b>
                  ) : (
                    <Lock size={12} />
                  )}
                  <StatusBadge status="pending" />
                </div>
              ))
            ) : (
              <div className="sb-empty" data-testid="pending-bets-empty">No pending bets</div>
            )}
            {!isPremium && botPending.length > 0 && !unlockedMatches.includes('bot-pending') && (
              <button
                className="sb-unlock gold-btn"
                onClick={() => handleUnlock('bot-pending', 4)}
                data-testid="button-unlock-bot-pending"
              >
                Unlock live picks · 4 SkillCoins
              </button>
            )}
          </section>

          <section className="sb-card">
            <div className="sb-head"><h2><TrendingUp size={15} /> Last simulation result</h2></div>
            {predsLoading ? (
              <div className="sb-skeleton short" data-testid="last-simulation-loading" />
            ) : lastResult ? (
              <>
                <div className="sb-result">
                  <div>
                    You picked: <b>{lastResult.outcome || lastResult.away_team || lastResult.home_team}</b>
                    <StatusBadge status={lastResult.status} />
                  </div>
                  <strong className={lastResult.status === 'won' ? 'positive' : 'negative'}>
                    {lastResult.status === 'won' ? 'Won +' : 'Lost -'}
                    {fmtZAR(Math.abs((lastResult.status === 'won'
                      ? (lastResult.payout_zar || 0) - (lastResult.stake_zar || 0)
                      : lastResult.stake_zar || 0) / 100))}
                  </strong>
                  <small>{lastResult.home_team} vs {lastResult.away_team}</small>
                </div>
                {!isPremium && (
                  <button className="sb-unlock gold-btn" onClick={() => router.push('/analytics')}>
                    See what you missed · 1 SkillCoin
                  </button>
                )}
              </>
            ) : (
              <div className="sb-empty" data-testid="last-simulation-empty">No simulations yet — place your first bet.</div>
            )}
          </section>

          <section className="sb-card sb-results">
            <div className="sb-head">
              <h2><BarChart3 size={15} /> SkillBot recent results</h2>
              <Link href="/sports-hub" className="sb-text-btn">View all</Link>
            </div>
            {botBetsLoading || botStatsLoading ? (
              <div className="sb-skeleton short" data-testid="recent-results-loading" />
            ) : botResults.length ? (
              <>
                <div className="sb-table">
                  {botResults.map((bet, i) => {
                    const pnl = bet.status === 'won' ? bet.payout_zar - bet.stake_zar : -bet.stake_zar
                    const settledDate = bet.settled_at
                      ? new Date(bet.settled_at).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })
                      : ''
                    return (
                      <div key={bet.id || i}>
                        <small>{settledDate}</small>
                        <span>{bet.match}</span>
                        <StatusBadge status={bet.status} />
                        <b className={pnl >= 0 ? 'positive' : 'negative'}>
                          {pnl >= 0 ? '+' : '-'}{fmtZAR(Math.abs(pnl))}
                        </b>
                      </div>
                    )
                  })}
                </div>
                <div className="sb-summary">
                  <div><small>Win rate</small><b>{botWinRate}%</b></div>
                  <div><small>Total bets</small><b>{botStats?.total_bets ?? botResults.length}</b></div>
                  <div>
                    <small>Total profit</small>
                    <b className={botPnl >= 0 ? 'positive' : 'negative'}>
                      {botPnl >= 0 ? '+' : '-'}{fmtZAR(Math.abs(botPnl))}
                    </b>
                  </div>
                </div>
              </>
            ) : (
              <div className="sb-empty" data-testid="recent-results-empty">No bot results yet</div>
            )}
          </section>
        </div>

        {!isPremium && (
          <section className="sb-premium">
            <div>
              <Crown size={22} />
              <div><strong>Go premium</strong><small>Unlock the full power of SkillBets.</small></div>
            </div>
            <div className="sb-benefits">
              Unlimited AI simulator · AI Six multi-pick slips · Advanced analytics · Priority access
            </div>
            <button className="sb-btn gold-btn" onClick={() => router.push('/subscribe')}>Go premium</button>
          </section>
        )}
      </div>

      <UnlockModal
        isOpen={unlockModal.isOpen}
        onClose={() => setUnlockModal({ isOpen: false, matchId: null, coinCost: 1 })}
        matchId={unlockModal.matchId}
        coinCost={unlockModal.coinCost}
        onUnlocked={handleUnlocked}
      />
    </main>
  )
}